import type { WebPushAutoPromptConfig } from "@jti/fe-common";

import { isIOSOrIPadOS } from "../../hooks/usePlatformDetector/platformDetector";

export const normalizePathname = (pathname: string): string => {
  const normalized = `/${pathname.split(/[?#]/, 1)[0] ?? ""}`.replace(/\/+/g, "/").toLowerCase();
  return normalized.length > 1 ? normalized.replace(/\/+$/, "") : normalized;
};

const removeLocalePrefix = (pathname: string, locale: string): string => {
  const normalizedPathname = normalizePathname(pathname);
  const normalizedLocale = locale.trim().replaceAll("_", "-").toLowerCase();
  const localePrefixes = new Set([normalizedLocale, normalizedLocale.split("-", 1)[0]]);
  const [pathnameLocale, ...remainingSegments] = normalizedPathname.slice(1).split("/");

  if (!localePrefixes.has(pathnameLocale)) return normalizedPathname;
  return remainingSegments.length > 0 ? `/${remainingSegments.join("/")}` : "/";
};

const patternMatches = (pathname: string, pattern: string): boolean => {
  const normalizedPattern = normalizePathname(pattern);
  const hasTrailingGlob = normalizedPattern.endsWith("/**");
  const patternWithoutTrailingGlob = hasTrailingGlob ? normalizedPattern.slice(0, -3) : normalizedPattern;
  const escaped = patternWithoutTrailingGlob.replace(/[.+?^${}()|[\]\\]/g, "\\$&");
  const regex = escaped.replace(/\*\*/g, "\0").replace(/\*/g, "[^/]*").replace(/\0/g, ".*");
  return new RegExp(`^${regex}${hasTrailingGlob ? "(?:/.*)?" : ""}$`, "i").test(pathname);
};

export const isWebPushConfigValid = (
  config: WebPushAutoPromptConfig | undefined
): config is Required<WebPushAutoPromptConfig> =>
  Boolean(
    config &&
    typeof config.configurationVersion === "string" &&
    config.configurationVersion.trim() &&
    Number.isFinite(config.cooldownDays) &&
    Number(config.cooldownDays) >= 0 &&
    Number.isInteger(config.maxImpressions) &&
    Number(config.maxImpressions) > 0 &&
    Array.isArray(config.includedPagePatterns) &&
    config.includedPagePatterns.length > 0 &&
    config.includedPagePatterns.every((pattern) => typeof pattern === "string" && pattern.trim()) &&
    Array.isArray(config.excludedPagePatterns) &&
    config.excludedPagePatterns.every((pattern) => typeof pattern === "string" && pattern.trim())
  );

export const hasWebPushStorageIdentity = (tenant?: string, locale?: string): boolean =>
  Boolean(tenant?.trim() && locale?.trim());

export const isEligiblePathname = (
  pathname: string,
  locale: string,
  config: Required<WebPushAutoPromptConfig>
): boolean => {
  const normalizedPathname = removeLocalePrefix(pathname, locale);
  if (config.excludedPagePatterns.some((pattern) => patternMatches(normalizedPathname, pattern))) return false;
  return config.includedPagePatterns.some((pattern) => patternMatches(normalizedPathname, pattern));
};

export const isWebPushPlatformEligible = (): boolean =>
  typeof window !== "undefined" &&
  window.isSecureContext === true &&
  !isIOSOrIPadOS() &&
  "Notification" in window &&
  "PushManager" in window &&
  "serviceWorker" in navigator;
